import _ from 'lodash';
import { useState, useMemo } from "react";
import { useMsal, } from '@azure/msal-react';
import Comparer from "../../app/comparer";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import { GoogleMap } from '../../components/map/map';
import { MetaDataTable } from '../../components/properties/metadata-table';
import LoadingAnimation from "../loading/loading";
import { FloatingEmailInput } from "../reusable-elements/floating-email-input";
import { FloatingTextInput } from "../reusable-elements/floating-text-input";
import { validEmail, validUKPostCode, notNullOrEmpty, validUKPhoneNumber, validateField } from "../../utils/validators";
import { ImageUploadModal } from "../files/image-upload-modal";
import { FileApi } from "../../api/file/file-api";
import { googleMapsConfig } from '../../app';

import './company-styles.css'

export const LandlordDetailsTab = ({ landlord, saveLandlord }) => {

    const { instance, inProgress, accounts } = useMsal();

    const fileApi = useMemo(() => new FileApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);

    const comparer = new Comparer();

    const defaultAddress = {
        doorNumberOrName: '',
        roadName: '',
        town: '',
        postalCode: ''
    };

    const getAddress = obj => obj && obj.address ? obj.address : defaultAddress;

    const [state, setState] = useState(structuredClone({ ...landlord, address: getAddress(landlord) }));
    const [originalState, setOriginalState] = useState(structuredClone({ ...landlord, address: getAddress(landlord) }));
    const [validationErrors, setValidationErrors] = useState(null);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [showLogoUploadModal, setShowLogoUploadModal] = useState(false);
    const [logoUploadProgress, setLogoUploadProgress] = useState(0);
    const [logoUploadInProgress, setLogoUploadInProgress] = useState(false);
    const [rerender, setRerender] = useState(false);

    const mapConfig = ({ lat, lng }) => {
        return {
            className: "h-100 w-100",
            style: { height: '100vh', width: '100%' },
            apiKey: googleMapsConfig.apiKey,
            center: {
                lat: lat,
                lng: lng
            },
            zoom: 17
        }
    };

    const validateState = stateToValidate => {
        validateField(() => stateToValidate.displayName && stateToValidate.displayName.length > 0, () => stateToValidate.displayName, setValidationErrors);
        validateField(() => stateToValidate.name && stateToValidate.name.length > 0, () => stateToValidate.name, setValidationErrors);
        validateField(() => stateToValidate.email && stateToValidate.email.length > 0 && validEmail(stateToValidate.email), () => stateToValidate.email, setValidationErrors);
        validateField(() => stateToValidate.phoneNumber && stateToValidate.phoneNumber.length > 0 && validUKPhoneNumber(stateToValidate.phoneNumber), () => stateToValidate.phoneNumber, setValidationErrors);
        validateField(() => stateToValidate.address.doorNumberOrName && stateToValidate.address.doorNumberOrName.length > 0, () => stateToValidate.address.doorNumberOrName, setValidationErrors);
        validateField(() => stateToValidate.address.roadName && stateToValidate.address.roadName.length > 0, () => stateToValidate.address.roadName, setValidationErrors);
        validateField(() => stateToValidate.address.town && stateToValidate.address.town.length > 0, () => stateToValidate.address.town, setValidationErrors);
        validateField(() => stateToValidate.address.postalCode && stateToValidate.address.postalCode.length > 0 && validUKPostCode(stateToValidate.address.postalCode), () => stateToValidate.address.postalCode, setValidationErrors);
    };

    const handleStateUpdates = updateMethod => {
        setState(current => {
            const newState = { ...current, address: { ...current.address } };
            updateMethod(newState);
            validateState(newState);
            return newState;
        });
        setRerender(!rerender);
    };

    const hasChanges = () => {
        const equal = comparer.equals(state, originalState);
        return !equal;
    };

    const canSave = () => {
        let result = hasChanges();
        result &&= validationErrors ? Object.keys(validationErrors).length === 0 : result;
        return result;
    };

    //const image = landlord && Object.hasOwn(landlord, 'logo') ? landlord.logo :

    const hasLogo = !!state && !!Object.hasOwn(state, 'logoPathIncSasToken');

    const landLordLogoPath = landlord => {
        return landlord.logoPathIncSasToken;
    }

    const imageStyle = landlord => {
        const unknown = { backgroundImage: 'url(/assets/img/pos/unknown-house.png)' };
        const known = { backgroundImage: `url(${landLordLogoPath(landlord)})` };
        return landLordLogoPath(landlord) ? known : unknown;
    }

    const handleCancel = () => {
        setState(structuredClone(originalState));
        setOriginalState(structuredClone(originalState));
        setValidationErrors(null);
    };

    const uploadFiles = async (path, collection) => {
        try {
            if (!logoUploadInProgress) {
                setLogoUploadInProgress(true);
                const totalSize = _.sumBy(collection, f => f.size);
                const files = await fileApi.uploadFiles(state.parent.name, path, collection, data => {
                    const totalRemaining = _.sumBy(data, d => d.remainingBytes);
                    const progress = 1 - (totalRemaining / totalSize);
                    console.log(`Progress: ${progress * 100} %`);
                    setLogoUploadProgress(progress);
                });
                setLogoUploadInProgress(false);
                setLogoUploadProgress(0);
                return files;
            }
        } catch (error) {
            console.log("Error occurred persisting files.", error);
        }
        finally {
            setShowLogoUploadModal(false)
        }
    };

    const handleSave = async updatedLandlord => {
        if (!saveInProgress) {
            setSaveInProgress(true);
            const result = await saveLandlord(updatedLandlord ?? state);
            setState(result.landlord);
            setOriginalState(result.landlord);
            setSaveInProgress(false);
        }
    };

    const updateLogo = async images => {
        const result = await uploadFiles(`${state.id}/logo`, images);
        const logoPath = result[0].path;
        const updatedLandlord = { ...state, logoPath: logoPath };
        await handleSave(updatedLandlord);
        setRerender(!rerender);
    };

    return !!state ? (

        <div className="row mb-3">

            <ImageUploadModal
                show={showLogoUploadModal}
                maxFileCount={1}
                handleCancel={() => setShowLogoUploadModal(false)}
                imageUploadInProgress={logoUploadInProgress}
                imageUploadProgress={logoUploadProgress}
                handleSave={updateLogo}
            />

            <div className="col-xl-4" key={"Details"}>
                <Panel
                    showReload={false}
                    showDelete={false}
                >
                    <PanelHeader>Details</PanelHeader>
                    <PanelBody>
                        <div className="row mb-15px">
                            <label className="form-label col-form-label col-md-3">Logo</label>
                            <div className={'col-md-6'}>
                                <div className="h-250px">
                                    {hasLogo &&
                                        <div onClick={() => setShowLogoUploadModal(true)} className="container img" style={imageStyle(state)}>
                                            <div className='overlay' >
                                                <button className="transparent-btn">Change</button>
                                            </div>
                                        </div>}
                                    {!hasLogo && <div onClick={() => setShowLogoUploadModal(true)}>Please upload a logo or image</div>}
                                </div>
                            </div>
                        </div>

                        <FloatingTextInput
                            className={"mt-1"}
                            label={`${!state.displayName ? "Enter" : ""} Display Name`}
                            text={state.displayName}
                            updateCallback={newValue => { handleStateUpdates(v => { v.displayName = newValue; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.displayName); }}
                        />

                        <FloatingTextInput
                            className={"mt-1"}
                            label={`${!state.name ? "Enter" : ""} System Name`}
                            text={state.name}
                            updateCallback={newValue => { handleStateUpdates(v => { v.name = newValue; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.name); }}
                            readOnly={true}
                        />

                        <FloatingEmailInput
                            className={"mt-1"}
                            label={`${!state.email ? "Enter" : ""} Email Address`}
                            email={state.email}
                            updateCallback={newValue => { handleStateUpdates(v => { v.email = newValue; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.email) && validEmail(state.email); }}
                        />

                        <FloatingTextInput
                            className={"mt-1"}
                            label={`${!state.phoneNumber ? "Enter" : ""} Phone Number`}
                            text={state.phoneNumber}
                            updateCallback={newValue => { handleStateUpdates(v => { v.phoneNumber = newValue; }) }}
                            required={true}
                            validatorFn={() => { return notNullOrEmpty(state.phoneNumber) && validUKPhoneNumber(state.phoneNumber); }}
                        />

                        {state.address &&
                            <>
                                <FloatingTextInput
                                    className={"mt-1"}
                                    label={`${!state.address.doorNumberOrName ? "Enter" : ""} Building Number/Name`}
                                    text={state.address.doorNumberOrName}
                                    updateCallback={newValue => { handleStateUpdates(v => { v.address.doorNumberOrName = newValue; }) }}
                                    required={true}
                                    validatorFn={() => { return notNullOrEmpty(state.address.doorNumberOrName); }}
                                />

                                <FloatingTextInput
                                    className={"mt-1"}
                                    label={`${!state.address.roadName ? "Enter" : ""} Street Name`}
                                    text={state.address.roadName}
                                    updateCallback={newValue => { handleStateUpdates(v => { v.address.roadName = newValue; }) }}
                                    required={true}
                                    validatorFn={() => { return notNullOrEmpty(state.address.roadName); }}
                                />

                                <FloatingTextInput
                                    className={"mt-1"}
                                    label={`${!state.address.town ? "Enter" : ""} Town`}
                                    text={state.address.town}
                                    updateCallback={newValue => { handleStateUpdates(v => { v.address.town = newValue; }) }}
                                    required={true}
                                    validatorFn={() => { return notNullOrEmpty(state.address.town); }}
                                />

                                <FloatingTextInput
                                    className={"mt-1"}
                                    label={`${!state.address.postalCode ? "Enter" : ""} Post Code`}
                                    text={state.address.postalCode}
                                    updateCallback={newValue => { handleStateUpdates(v => { v.address.postalCode = newValue; }) }}
                                    required={true}
                                    validatorFn={() => { return validUKPostCode(state.address.postalCode); }}
                                />
                            </>
                        }

                        <div className="mt-3">
                            {canSave() &&
                                <button title='Save' className="btn btn-primary me-10px" onClick={() => handleSave(state)}>
                                    <i title='Save' className="fa fa-save"></i>&nbsp;&nbsp;Save
                                </button>
                            }
                            {hasChanges() &&
                                <button title='Cancel' className="btn btn-warning" onClick={handleCancel}>
                                    <i title='Cancel' className="fa fa-cancel"></i>&nbsp;&nbsp;Cancel
                                </button>
                            }
                        </div>

                        <MetaDataTable data={state} />
                    </PanelBody>
                </Panel>
            </div>
            <div className="col-xl-8" key={"Map"}>
                <Panel
                    showReload={false}
                    showDelete={false}
                >
                    <PanelHeader>Map</PanelHeader>
                    <PanelBody>
                        <div>
                            {state.coordinates ? <GoogleMap {...mapConfig({ lat: state.coordinates.latitude, lng: state.coordinates.longitude })}
                                markers={[{
                                    icon: "fa fa-location-dot",
                                    name: "HQ",
                                    ...mapConfig({ lat: state.coordinates.latitude, lng: state.coordinates.longitude })
                                }
                                ]}>
                            </GoogleMap> : <>Not Yet Geocoded. Check the address.</>}
                        </div>
                    </PanelBody>
                </Panel>
            </div>
        </div>

    )
        :
        <LoadingAnimation />
}